import React from 'react';
import styled from 'styled-components';
import { Influencer } from '../../types/influencer';

interface InfluencerCardProps {
  influencer: Influencer;
  isPreview?: boolean;
  businessEmail?: string;
  index?: number;
  selectedCountryName?: string;
  selectedIndustryName?: string;
}

const CardWrapper = styled.div`
  position: relative;
  
  /* Remove the hover transform that didn't add value */
  &:hover {
    transform: none;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.boxShadow.sm};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  overflow: visible;
  padding: ${({ theme }) => theme.spacing[4]};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: grid;
    // Tablet: 2-column layout. Col1: Profile, Info. Col2: Match, Description.
    grid-template-columns: minmax(220px, 1fr) minmax(300px, 1fr); 
    grid-template-areas:
      "profile content"
      "info    content";
    gap: ${({ theme }) => theme.spacing[3]};
    align-items: start; // Align items to the start of their grid area
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    // Desktop: Revert to original 4-column layout, but ensure it's well-defined
    grid-template-columns: minmax(220px, 0.75fr) 1fr 95px minmax(300px, 1.25fr);
    grid-template-areas: "profile info match description"; // Explicitly define areas
    grid-auto-flow: column; // This might not be needed with template-areas
    align-items: start; 
  }

  &:hover {
    box-shadow: ${({ theme }) => theme.boxShadow.lg};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ProfileSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => `rgba(248, 249, 252, 0.5)`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-area: profile; // Assign to grid area
    justify-content: flex-start;
    padding: ${({ theme }) => theme.spacing[3]};
    // height: 100%; // Removed, align-items:start on parent Card is enough
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-column: 1; // Revert to column for desktop if not using areas, or keep area
    height: auto; // Revert to auto height
  }
`;

const ProfileImage = styled.img`
  width: 50px;
  height: 50px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  object-fit: cover;
  border: none;
  box-shadow: ${({ theme }) => theme.boxShadow.md};
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing[1]};
  width: 100%;
  overflow: hidden;
`;

const Name = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  line-height: 1.2;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  width: 100%;
`;

const Username = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textLight};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  overflow-wrap: break-word;
  word-break: break-all;
  max-width: 100%;
`;

const FollowerCount = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: ${({ theme }) => theme.spacing[1]};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => `rgba(248, 249, 252, 0.3)`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-area: info; // Assign to grid area
    margin-top: 0;
    padding: ${({ theme }) => theme.spacing[3]};
    align-self: start;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-column: 2; // Revert to column for desktop
  }
`;

const DemographicsRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  width: 100%;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: row;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[4]};
    flex-wrap: nowrap;
    margin-bottom: 0;
  }
`;

const DemographicGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[1]};
  
  &:last-child {
    flex: 1;
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: row;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
  }
`;

const DemographicLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const GenderDistribution = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    margin-bottom: 0;
  }
`;

const GenderItem = styled.div<{ color: string }>`
  background-color: ${({ color, theme }) => `${color}15`}; /* Using 15% opacity */
  color: ${({ color }) => color};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  display: inline-flex;
  align-items: center;
  border: 1px solid ${({ color }) => `${color}30`}; /* Using 30% opacity */
  white-space: nowrap;
  max-width: 100%;
`;

const InterestsList = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing[2]};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-top: ${({ theme }) => theme.spacing[3]};
  }
`;

const InterestTag = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  background-color: white;
  color: ${({ theme }) => theme.colors.text};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: all 0.2s ease;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  
  &:hover {
    background-color: ${({ theme }) => `rgba(67, 97, 238, 0.05)`};
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-1px);
  }
`;

// Optimize the DonutChart components
const DonutChart = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08));
`;

const DonutSVG = styled.svg`
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
`;

const DonutBackground = styled.circle`
  fill: none;
  stroke: ${({ theme }) => theme.colors.backgroundAlt};
  stroke-width: 6;
`;

const DonutProgress = styled.circle<{ percentage: number }>`
  fill: none;
  stroke-width: 6;
  stroke: ${({ theme, percentage }) => {
    // More sleek, monochromatic color palette
    if (percentage >= 80) return '#3ECF8E'; // Subdued green
    if (percentage >= 70) return '#4361EE'; // Brand blue
    if (percentage >= 60) return '#4895EF'; // Light blue
    if (percentage >= 40) return '#8E8FF7'; // Soft purple
    if (percentage >= 20) return '#F7A76B'; // Soft orange
    return '#FF6B6B'; // Soft red
  }};
  stroke-linecap: round;
  stroke-dasharray: 126; /* Circumference of circle with r=20 (2πr) */
  stroke-dashoffset: ${({ percentage }) => 126 - (percentage / 100 * 126)};
  transition: stroke-dashoffset 0.5s ease;
`;

const DonutLabel = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.text};
`;

// Unused components that we're disabling ESLint warnings for
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MatchDescriptionSection = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => `rgba(248, 249, 252, 0.3)`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

const MatchDescription = styled.div`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  line-height: 1.6;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  width: 100%;
  max-width: 100%;
  flex: 1;
  overflow: hidden;
  
  p {
    margin: 0 0 ${({ theme }) => theme.spacing[3]};
    hyphens: auto;
    overflow: hidden;
    word-break: break-word;
    max-width: 100%;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  em {
    color: ${({ theme }) => theme.colors.textLight};
    font-style: italic;
    display: block;
    margin-top: ${({ theme }) => theme.spacing[2]};
    font-size: ${({ theme }) => theme.fontSizes.xs};
    line-height: 1.4;
    max-width: 100%;
    word-break: break-word;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MatchStrength = styled.span<{ percentage?: number }>`
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ percentage = 0 }) => {
    if (percentage >= 80) return '#3ECF8E'; 
    if (percentage >= 70) return '#4361EE';
    if (percentage >= 60) return '#4895EF';
    if (percentage >= 40) return '#8E8FF7';
    if (percentage >= 20) return '#F7A76B';
    return '#FF6B6B';
  }};
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.fontSizes.md};
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  transition: all 0.2s ease;
  border: 1px solid ${({ theme }) => theme.colors.border};
  
  &:hover {
    background-color: ${({ theme }) => `rgba(67, 97, 238, 0.08)`};
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  img {
    width: 16px;
    height: 16px;
    object-fit: contain;
    transition: all 0.2s ease;
  }
  
  &:hover img {
    filter: brightness(1.1);
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ObfuscatedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(5px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LockIcon = styled.div`
  width: 50px;
  height: 50px;
  background-color: ${({ theme }) => theme.colors.backgroundDark};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  color: white;
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const UnlockText = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  max-width: 80%;
`;

// New wrapper for the second column content (Match and Description)
const ContentColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-area: content; // This wrapper occupies the 'content' grid area for tablet
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: contents; // For desktop, this wrapper "disappears", letting its children be direct grid items of Card
  }
`;

const MatchSection = styled.div`
  display: flex;
  // flex-direction: column; // Keep internal flex direction as needed, but not for stacking with DescriptionSection
  align-items: flex-start;
  justify-content: flex-start;
  // margin-top: ${({ theme }) => theme.spacing[3]}; // No longer needed here for stacking
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => `rgba(248, 249, 252, 0.3)`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    // grid-area: content; // No longer assigned here, parent ContentColumnWrapper handles it
    margin-top: 0; // Reset margin, spacing handled by ContentColumnWrapper gap
    height: auto;
    align-self: start;
    text-align: left;
    width: 100%;
    // padding: ${({ theme }) => theme.spacing[3]}; // Padding already defined above
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-column: 3; // Revert to column for desktop
    justify-content: center; 
    align-items: center;    
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MatchLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StarIcon = styled.span`
  color: #FFD700;
  font-size: ${({ theme }) => theme.fontSizes.lg};
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SectionTitle = styled.h4`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing[2]};
  width: 100%;
  text-align: left;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-top: ${({ theme }) => theme.spacing[1]};
  }
`;

const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;
  // margin-top: ${({ theme }) => theme.spacing[3]}; // No longer needed here for stacking
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => `rgba(248, 249, 252, 0.3)`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  min-height: fit-content;
  max-width: 100%;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    // grid-area: content; // No longer assigned here, parent ContentColumnWrapper handles it
    margin-top: 0; // Reset margin, spacing handled by ContentColumnWrapper gap
    height: auto;
    overflow: hidden;
    width: 100%;
    align-self: start;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-column: 4; // Revert to column for desktop
  }
`;

const AgeInfo = styled.div`
  background-color: rgba(100, 116, 139, 0.1);
  color: ${({ theme }) => theme.colors.text};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  white-space: nowrap;
`;

const EngagementRateInfo = styled.div`
  background-color: rgba(100, 116, 139, 0.1);
  color: ${({ theme }) => theme.colors.text};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  white-space: nowrap;
`;

const UpgradeMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[4]};
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  gap: ${({ theme }) => theme.spacing[3]};
`;

const UpgradeIcon = styled.div`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  color: ${({ theme }) => theme.colors.primary};
`;

const UpgradeText = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  max-width: 80%;
`;

const UpgradeButton = styled.a`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #3D8BF0; /* Slightly darker shade of primary */
  }
`;

const BlurOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(5px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const InfluencerCard: React.FC<InfluencerCardProps> = ({
  influencer,
  isPreview = false,
  businessEmail = '',
  index = 0,
  selectedCountryName,
  selectedIndustryName
}) => {
  // Helper function to format numbers (e.g., 10000 to 10k)
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    }
    return num.toString();
  };

  // Generate AI match description based on influencer data
  const getAIMatchDescription = (): string => {
    // Directly return the matchDescription from the influencer's rating object.
    // The data service (csvDataService.ts) already provides a fallback if the CSV doesn't have one.
    return influencer.rating?.matchDescription || 'Strong performer with consistent quality content in their niche.';
  };

  return (
    <CardWrapper>
      <Card>
        <ProfileSection>
          <ProfileImage src={influencer.profileImage} alt={influencer.name} />
          <ProfileInfo>
            <Name>{influencer.name}</Name>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px', gap: '8px' }}>
              <span title="Email Available" style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}>
                <img src="/icons/envelope-check-svgrepo-com.svg" alt="Email Available" style={{ width: '25px', height: '25px' }} />
              </span>
              <span title="Verified Audience" style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}>
                <img src="/icons/shield-check-circle-svgrepo-com.svg" alt="Verified Audience" style={{ width: '25px', height: '25px' }} />
              </span>
            </div>
            <Username>@{influencer.username}</Username>
            <FollowerCount>{formatNumber(influencer.followers)} Followers</FollowerCount>
            <SocialLinks>
              {influencer.instagramUrl && (
                <SocialLink 
                  href={influencer.instagramUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  title="Instagram Profile"
                >
                  <img src="/icons/instagram-svgrepo-com.svg" alt="Instagram" />
                </SocialLink>
              )}
              {influencer.tiktokUrl && (
                <SocialLink 
                  href={influencer.tiktokUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  title="TikTok Profile"
                >
                  <img src="/icons/tiktok-svgrepo-com.svg" alt="TikTok" />
                </SocialLink>
              )}
            </SocialLinks>
          </ProfileInfo>
        </ProfileSection>
        
        <InfoSection>
          <DemographicsRow>
            <DemographicGroup>
              <DemographicLabel>GENDER:</DemographicLabel>
              <GenderDistribution>
                <GenderItem color="#4361EE">M {influencer.audienceGender.male}%</GenderItem>
                <GenderItem color="#FF6B6B">F {influencer.audienceGender.female}%</GenderItem>
              </GenderDistribution>
            </DemographicGroup>
                </DemographicsRow>
            
                <DemographicsRow>
            <DemographicGroup>
              <DemographicLabel>TOP AGE</DemographicLabel>
              <AgeInfo>
                {Object.entries(influencer.audienceAge)
                  .filter(([, percentage]) => percentage > 20)
                  .map(([range]) => range)
                  .join(', ')}
              </AgeInfo>
            </DemographicGroup>
                  </DemographicsRow>
          <DemographicsRow>
            {selectedCountryName && (
              <DemographicGroup>
                <DemographicLabel>TOP COUNTRY</DemographicLabel>
                <GenderItem color="#1E293B">{selectedCountryName}</GenderItem>
              </DemographicGroup>
            )}
          </DemographicsRow>
          
          {influencer.engagementRate !== undefined && (
            <DemographicGroup>
              <DemographicLabel>ENGAGEMENT</DemographicLabel>
              <EngagementRateInfo>{influencer.engagementRate.toFixed(1)}%</EngagementRateInfo>
            </DemographicGroup>
          )}
          
          <InterestsList>
            {influencer.audienceInterests
              .filter(interest => interest.percentage > 40)
              .map(interest => (
                <InterestTag key={interest.name}>{interest.name}</InterestTag>
              ))}
            {selectedIndustryName && (
              <InterestTag key={selectedIndustryName}>{selectedIndustryName}</InterestTag>
            )}
          </InterestsList>
        </InfoSection>
        
        {/* Wrap MatchSection and DescriptionSection for md breakpoint stacking */}
        <ContentColumnWrapper>
          <MatchSection>
            <DonutChart>
              <DonutSVG viewBox="0 0 50 50">
                <DonutBackground cx="25" cy="25" r="20" />
                <DonutProgress 
                  cx="25" 
                  cy="25" 
                  r="20" 
                  percentage={influencer.matchScore || influencer.rating.aiMatch || 85} 
                />
              </DonutSVG>
              <DonutLabel>{influencer.matchScore || influencer.rating.aiMatch || 85}%</DonutLabel>
            </DonutChart>
          </MatchSection>
          
          <DescriptionSection>
            <MatchDescription>
              <p>{getAIMatchDescription()}</p>
            </MatchDescription>
          </DescriptionSection>
        </ContentColumnWrapper>
      </Card>

      {isPreview && (
        <BlurOverlay>
          <UpgradeMessage>
            <UpgradeIcon>🔍</UpgradeIcon>
            <UpgradeText>Unlock access to all influencers + detailed analytics</UpgradeText>
            <UpgradeButton 
              href={`https://business.hypefy.ai/auth/signup?email=${encodeURIComponent(businessEmail)}`} 
              target="_blank"
              rel="noopener noreferrer"
            >
              Get Full Access
            </UpgradeButton>
          </UpgradeMessage>
        </BlurOverlay>
      )}
    </CardWrapper>
  );
};

export default InfluencerCard;
